import { EOrderPlaceType } from "../../Enum/EOrderPlaceType"
import { ERestaurantServiceType } from "../../Enum/ERestaurantServiceType"
import { calculateDistanceInMeters } from "../../utils/geo"

export const DEFAULT_LOCAL_SERVICE_RADIUS_METERS = 100

const hasValidCoordinates = (latitude?: number, longitude?: number): latitude is number =>
  typeof latitude === "number" &&
  Number.isFinite(latitude) &&
  typeof longitude === "number" &&
  Number.isFinite(longitude)

const resolvePosition = async (): Promise<{ latitude: number; longitude: number } | null> => {
  if (!navigator.geolocation) {
    return null
  }

  return await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
      () => resolve(null),
      {
        maximumAge: 300000,
        timeout: 8000,
        enableHighAccuracy: false,
      },
    )
  })
}

interface ResolveOrderModeByProximityParams {
  restaurantServiceType: ERestaurantServiceType
  restaurantLatitude?: number
  restaurantLongitude?: number
  localServiceRadiusMeters?: number
}

export class OrderModeProximityHelper {
  static async resolveInitialOrderMode(params: ResolveOrderModeByProximityParams): Promise<EOrderPlaceType> {
    if (params.restaurantServiceType === ERestaurantServiceType.Local) {
      return EOrderPlaceType.Local
    }

    if (params.restaurantServiceType === ERestaurantServiceType.Delivery) {
      return EOrderPlaceType.Delivery
    }

    if (!hasValidCoordinates(params.restaurantLatitude, params.restaurantLongitude)) {
      return EOrderPlaceType.Delivery
    }

    const currentPosition = await resolvePosition()

    if (!currentPosition) {
      return EOrderPlaceType.Delivery
    }

    const radiusInMeters =
      typeof params.localServiceRadiusMeters === "number" && params.localServiceRadiusMeters > 0
        ? params.localServiceRadiusMeters
        : DEFAULT_LOCAL_SERVICE_RADIUS_METERS

    const distanceInMeters = calculateDistanceInMeters(
      currentPosition.latitude,
      currentPosition.longitude,
      params.restaurantLatitude,
      params.restaurantLongitude,
    )

    return distanceInMeters <= radiusInMeters ? EOrderPlaceType.Local : EOrderPlaceType.Delivery
  }
}
